import { useCallback, useEffect, useRef, useState } from 'react';
import { gymApi } from '../api/gymApi';
import { useAuthed } from './useAuthed';
import type { SessionExercise, WorkoutSession } from '../../../shared/types';

/**
 * Owns one workout session client-side.
 *
 * Edits are local and free. A save only happens at a point that actually means
 * something — finishing a set, undoing one, finishing the exercise — plus a
 * safety flush when the page is left or hidden so nothing typed is ever lost.
 * Stepping a weight ten times is one API call at the end, not ten.
 *
 * `saveError` is exposed so a genuine failure can still be surfaced; there is
 * deliberately no "Saving…"/"Saved" chatter, since a save is no longer
 * something the user triggers or needs to watch.
 */
export function useWorkoutSession(sessionId: string | undefined) {
  const authed = useAuthed();
  const [session, setSession] = useState<WorkoutSession | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saveError, setSaveError] = useState('');

  // The authoritative in-flight exercise list. Held in a ref as well as state
  // so `mutate` can compute the next value synchronously — a save fired in the
  // same tick as an edit must see that edit, which a state updater cannot
  // guarantee.
  const exercisesRef = useRef<SessionExercise[]>([]);
  /** Set when there are local changes the server has not been told about. */
  const dirty = useRef(false);

  const authedRef = useRef(authed);
  authedRef.current = authed;

  // Loads once per session id, deliberately NOT on `authed`. Re-running this
  // would re-fetch and overwrite local state — silently discarding sets the
  // lifter had entered but not yet flushed.
  useEffect(() => {
    if (!sessionId) { setLoading(false); return; }
    let cancelled = false;
    gymApi.getWorkout(sessionId, authedRef.current)
      .then(s => {
        if (cancelled) return;
        setSession(s);
        exercisesRef.current = s.exercises;
        dirty.current = false;
      })
      .catch(err => { if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load workout'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [sessionId]);

  /** Pushes local changes up. No-op when there is nothing outstanding. */
  const flush = useCallback(async () => {
    if (!sessionId || !dirty.current) return;
    const exercises = exercisesRef.current;
    dirty.current = false;
    try {
      await gymApi.saveWorkout(sessionId, exercises, authedRef.current);
      setSaveError('');
    } catch (err) {
      dirty.current = true; // keep it pending so a later flush retries
      setSaveError(err instanceof Error ? err.message : 'Could not save');
    }
  }, [sessionId]);

  /**
   * Applies a local change. Pass `save` at the points worth a round trip;
   * everything else just stays local until something does.
   */
  const mutate = useCallback((
    fn: (exercises: SessionExercise[]) => SessionExercise[],
    save = false,
  ) => {
    const next = fn(exercisesRef.current);
    if (next === exercisesRef.current && !save) return;
    exercisesRef.current = next;
    dirty.current = true;
    setSession(prev => (prev ? { ...prev, exercises: next } : prev));
    if (save) void flush();
  }, [flush]);

  // Safety net: never leave the page holding unsaved sets.
  useEffect(() => () => { void flush(); }, [flush]);

  useEffect(() => {
    const onHide = () => { if (document.hidden) void flush(); };
    document.addEventListener('visibilitychange', onHide);
    window.addEventListener('pagehide', onHide);
    return () => {
      document.removeEventListener('visibilitychange', onHide);
      window.removeEventListener('pagehide', onHide);
    };
  }, [flush]);

  return { session, setSession, loading, error, saveError, mutate, flush };
}
